import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Disc3, Hash, Bot, Barcode, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";
import { Hero } from "@/components/dashboard/Hero";
import { StatCard } from "@/components/dashboard/StatCard";
import { StreamsPanel } from "@/components/dashboard/StreamsPanel";
import { Pipeline } from "@/components/dashboard/Pipeline";
import { RecentList } from "@/components/dashboard/RecentList";
import { BotPanel, UpcomingPanel } from "@/components/dashboard/SidePanels";
import { stagger } from "@/components/dashboard/motion";

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const { user, can } = useAuth();
  const staff = can("admin", "manager");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/releases").then((r) => setRecent(r.data.slice(0, 6)));
    api.get("/analytics/overview").then((r) => setAnalytics(r.data)).catch(() => setAnalytics({ releases: [] }));
    if (staff) api.get("/jobs").then((r) => setJobs(r.data.filter((j) => ["queued", "running", "waiting_2fa"].includes(j.status))));
  }, [staff]);

  const s = stats || { by_status: {} };
  const spark = (analytics?.daily || []).slice(-14).map((d) => d.streams);
  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <Hero user={user} staff={staff} stats={stats} />

      <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Disc3} label={t("Lanzamientos")} value={s.releases_total} sub={t("{n} completados · {d} borradores", { n: s.by_status.completed || 0, d: s.by_status.draft || 0 })} testId="stat-releases" accent spark={spark.some((v) => v > 0) ? spark : undefined} />
        <StatCard icon={Barcode} label={t("UPC asignados")} value={s.upc_assigned} sub={t("de {n} lanzamientos", { n: s.releases_total ?? 0 })} testId="stat-upc" progress={pct(s.upc_assigned, s.releases_total)} />
        <StatCard icon={Hash} label={t("ISRC asignados")} value={s.isrc_assigned} sub={t("de {n} tracks", { n: s.tracks_total ?? 0 })} testId="stat-isrc" progress={pct(s.isrc_assigned, s.tracks_total)} />
        {staff ? (
          <StatCard icon={Bot} label={t("Bot activo")} value={s.active_jobs} sub={s.by_status.waiting_2fa ? t("{n} esperando 2FA", { n: s.by_status.waiting_2fa }) : t("sin jobs pendientes")} testId="stat-jobs" accent={!!s.active_jobs} />
        ) : (
          <StatCard icon={CheckCircle2} label={t("Aprobados")} value={s.by_status.approved ?? 0} sub={t("{n} en distribución", { n: s.by_status.completed || 0 })} testId="stat-approved" accent />
        )}
      </motion.div>

      <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <StreamsPanel data={analytics} />
        <Pipeline byStatus={s.by_status} staff={staff} top={analytics?.releases?.find((r) => r.social_views_7d > 0 || r.streams_7d?.value) || analytics?.releases?.[0]} />
      </motion.div>

      <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <RecentList recent={recent} />
        <div className="lg:col-span-4 space-y-6">
          {staff && <BotPanel jobs={jobs} />}
          <UpcomingPanel upcoming={stats?.upcoming} />
        </div>
      </motion.div>
    </div>
  );
}
